import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';

const PRERENDER_KEY = makeStateKey<boolean>('prerender');

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  webSiteUrl: string | undefined;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState
  ) {
  }

  /**
   * Check if we're actually in a prerender environment
   * This distinguishes between regular SSR and actual prerendering
   */
  private isActuallyPrerendering(): boolean {
    // Check for Angular Universal prerender flag
    if (typeof process !== 'undefined' && process.env) {
      // Angular Universal sets this during prerendering
      if (process.env['NG_PRERENDER'] === 'true') return true;
      
      // Our custom prerender flag
      if (process.env['PRERENDER'] === 'true') return true;
      
      // Check if npm script includes 'prerender'
      if (process.env['npm_lifecycle_event']?.includes('prerender')) return true;
      
      // Angular CLI prerender builder sets this
      if (process.env['NPM_COMMAND']?.includes('prerender')) return true;
    }
    
    // Check for global Angular Universal prerender flag
    if (typeof global !== 'undefined' && (global as any).__NG_PRERENDER__) {
      return true;
    }
    
    return false;
  }

  getUrlFromPrerender(){
     if (typeof process !== 'undefined' && process.env) {
      // Angular Universal sets this during prerendering
      if (process.env['WEBSITE']) 
        return process.env['WEBSITE'];
    }
    return this.webSiteUrl;
  }

  /**
   * Returns true if the code is running in a browser environment
   */
  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Returns true if the code is running on the server during SSR
   */
  get isServer(): boolean {
    return isPlatformServer(this.platformId);
  }

  get isPrerendered(): boolean {
    return this.isActuallyPrerendering();
  }

  /**
   * Runs the provided function only in the browser environment
   * @param fn Function to execute
   */
  runInBrowser(fn: () => void): void {
    if (this.isBrowser) {
      fn();
    }
  }

  /**
   * Runs the provided function only in the server environment
   * @param fn Function to execute
   */
  runOnServer(fn: () => void): void {
    if (this.isServer) {
      fn();
    }
  }

  /**
   * Debug method to understand the current environment state
   * Use this to troubleshoot prerender detection issues
   */
  getEnvironmentInfo(): any {
    const info: any = {
      isBrowser: this.isBrowser,
      isServer: this.isServer,
      isPrerendered: this.isPrerendered,
      hasTransferKey: this.transferState.hasKey(PRERENDER_KEY),
      transferValue: this.transferState.get(PRERENDER_KEY, undefined)
    };

    if (typeof process !== 'undefined' && process.env) {
      info.processEnv = {
        NG_PRERENDER: process.env['NG_PRERENDER'],
        PRERENDER: process.env['PRERENDER'],
        npm_lifecycle_event: process.env['npm_lifecycle_event'],
        NPM_COMMAND: process.env['NPM_COMMAND']
      };
    }

    if (typeof global !== 'undefined') {
      info.globalNgPrerender = (global as any).__NG_PRERENDER__;
    }

    return info;
  }
}
